<template>
    <div class="relative flex flex-col flex-1 min-h-0 bg-stage-background overflow-hidden">
        <div class="stage-page-header">
            <div class="stage-page-header-row">
                <div class="stage-page-header-main">
                    <div class="flex flex-wrap items-center gap-2">
                        <h1 class="stage-page-title">Dokumentation</h1>
                        <div class="stage-shortcut-badges">
                            <span
                                v-for="badge in $navigationShortcutBadges('documentation')"
                                :key="badge.key"
                                :title="badge.title || 'Shortcut'"
                                class="stage-shortcut-badge"
                            >
                                <svg-vue class="stage-shortcut-badge-icon" icon="font-awesome.solid.command"></svg-vue>
                                {{ badge.label }}
                            </span>
                        </div>
                    </div>
                </div>
                <div class="stage-page-header-actions">
                    <button
                        :aria-label="$t('Filter menu')"
                        type="button"
                        class="btn-stage-icon-plain stage-filter-icon-button"
                        :title="$t('Filter menu')"
                        @click="toggleFilterSidebar"
                    >
                        <svg-vue class="h-4 w-4" icon="font-awesome.regular.sliders"></svg-vue>
                        <span
                            v-if="hasActiveFilters"
                            aria-hidden="true"
                            class="stage-filter-active-dot"
                        ></span>
                    </button>
                    <button
                        :aria-label="$t('Reload')"
                        type="button"
                        class="btn-stage-icon-plain"
                        :title="$t('Reload')"
                        @click="getDocumentations"
                    >
                        <svg-vue class="h-4 w-4" icon="font-awesome.regular.arrows-repeat"></svg-vue>
                    </button>
                </div>
            </div>
        </div>

        <div class="stage-page-body stage-page-fill">
            <div class="w-full h-full bg-white shadow rounded-md relative overflow-hidden flex flex-col min-h-0">
                <loading :status="loading"/>

                <div class="relative flex-1 min-h-0 overflow-hidden">
                    <div class="h-full overflow-auto sm:overflow-hidden">
                        <template v-if="paginatedDocumentations.length > 0">
                            <div class="hidden sm:flex stage-listview-split">
                                <div class="stage-listview-split-header">
                                    <table class="stage-listview-split-table table-listview">
                                        <colgroup>
                                            <col class="w-[36%]">
                                            <col>
                                            <col class="w-[12rem]">
                                        </colgroup>
                                        <thead>
                                        <tr>
                                            <th class="table-listview-l-th w-[36%]">Titel</th>
                                            <th class="table-listview-l-th">Erstellt von</th>
                                            <th class="table-listview-l-th w-[12rem]">Letztes Update</th>
                                        </tr>
                                        </thead>
                                    </table>
                                </div>
                                <div class="stage-listview-split-body">
                                    <table class="stage-listview-split-table table-listview table-listview--zebra">
                                        <colgroup>
                                            <col class="w-[36%]">
                                            <col>
                                            <col class="w-[12rem]">
                                        </colgroup>
                                        <tbody class="bg-white">
                                        <template v-for="documentation in paginatedDocumentations" :key="documentation.id">
                                            <tr
                                                class="cursor-pointer"
                                                @click="$router.push(documentationManagePath(documentation.id))"
                                            >
                                                <td class="table-listview-l-td w-[36%]">
                                                    <div class="flex items-start min-w-0">
                                                        <div class="h-10 w-10 mr-3 text-gray-400 flex-shrink-0 flex items-center justify-center">
                                                            <svg-vue class="h-6 w-6" icon="font-awesome.regular.book-open-cover"></svg-vue>
                                                        </div>
                                                        <div class="min-w-0">
                                                            <div class="text-sm font-medium text-gray-900 truncate">
                                                                {{ documentation.title }}
                                                            </div>
                                                            <div class="text-sm text-gray-500 truncate max-w-xl">
                                                                {{ getBodyPreview(documentation.body_excerpt) }}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td class="table-listview-l-td whitespace-no-wrap">
                                                    <div class="text-sm text-gray-600">
                                                        {{ documentation.author || '-' }}
                                                    </div>
                                                </td>
                                                <td class="table-listview-l-td whitespace-no-wrap w-[12rem]">
                                                    <div class="text-sm text-gray-500">
                                                        {{ $momentFormatDateTimeAgo(documentation.updated_at) }}
                                                    </div>
                                                </td>
                                            </tr>
                                        </template>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </template>

                        <template v-else-if="!loading">
                            <empty-state />
                        </template>
                    </div>

                    <aside
                        :aria-hidden="!filterSidebarOpen"
                        class="stage-filter-sidebar stage-filter-panel w-80 overflow-auto z-20"
                        :class="filterSidebarOpen ? 'stage-filter-sidebar-open' : 'stage-filter-sidebar-closed'"
                    >
                        <div class="stage-filter-body">
                            <div class="stage-filter-title-row">
                                <svg-vue class="stage-filter-title-icon h-5 w-5 text-gray-600 mr-3" icon="font-awesome.regular.sliders"></svg-vue>
                                <div class="stage-filter-title">{{ $t('Filter & Sorting') }}</div>
                            </div>
                            <div class="stage-filter-label">{{ $t('Sort by') }}</div>
                            <div class="stage-filter-row mb-3">
                                <button
                                    class="stage-filter-sort-btn text-sm leading-5 font-medium"
                                    type="button"
                                    @click="toggleSortOrder"
                                >
                                    <svg-vue class="h-4 w-4 text-gray-400" icon="font-awesome.regular.arrow-down-arrow-up"/>
                                </button>
                                <div class="stage-filter-sort-select-wrap">
                                    <select
                                        v-model="sort.column"
                                        aria-label="Sort by"
                                        class="stage-filter-sort-select block form-select text-sm leading-5 font-medium"
                                        @change="onFilterChange"
                                    >
                                        <option value="title">Titel</option>
                                        <option value="author">Erstellt von</option>
                                        <option value="updated_at">Letztes Update</option>
                                    </select>
                                    <span class="stage-filter-select-icon">
                                        <svg-vue class="h-[0.875rem] w-[0.875rem] text-gray-400" icon="font-awesome.regular.angle-down"></svg-vue>
                                    </span>
                                </div>
                            </div>

                            <div class="stage-filter-label">{{ $t('Search') }}</div>
                            <div class="mb-4">
                                <div class="relative">
                                    <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <svg-vue class="h-4 w-4 text-gray-400" icon="font-awesome.search-regular"></svg-vue>
                                    </div>
                                    <input
                                        v-model.lazy="filters.search"
                                        :placeholder="$t('Search')"
                                        class="stage-filter-input form-input block pl-10 text-sm"
                                        @change="onFilterChange"
                                    >
                                </div>
                            </div>
                            <div class="stage-filter-label">Einträge pro Seite</div>
                            <div class="mb-3">
                                <input-select
                                    id="documentation-customer-per-page"
                                    v-model="perPage"
                                    class="stage-filter-select"
                                    :options="perPageOptions"
                                    option-label="name"
                                    @change="onPerPageChange"
                                />
                            </div>

                            <div v-if="hasActiveFilters" class="col-span-3">
                                <button
                                    id="documentation-customer-filter-reset-btn"
                                    type="button"
                                    class="text-sm text-gray-500 hover:text-gray-600 focus:outline-none"
                                    @click="resetFilters"
                                >
                                    {{ $t('Reset selection') }}
                                </button>
                            </div>
                        </div>
                    </aside>
                </div>

                <list-footer-pagination
                    :start="paginationStart"
                    :end="paginationEnd"
                    :total="paginationTotal"
                    :current-page="pagination.currentPage"
                    :total-pages="pagination.totalPages"
                    @prev="changePage(pagination.currentPage - 1)"
                    @next="changePage(pagination.currentPage + 1)"
                />
            </div>
        </div>
    </div>
</template>

<script>
import {
    queryNumber,
    queryString,
    queryWithoutKeys,
    routeWithReturnTo,
    sameRouteQuery,
    setQueryValue,
} from "@/utils/list-route-state";

export default {
    name: "documentation-customer-list",
    metaInfo() {
        return {
            title: "Dokumentation"
        }
    },
    data() {
        const routeQuery = (this.$route && this.$route.query) ? this.$route.query : {};
        const initialPage = queryNumber(routeQuery, 'list_page', 1);
        const initialPerPage = queryNumber(routeQuery, 'list_per_page', 10, [10, 25, 50, 100]);
        let initialSortColumn = queryString(routeQuery, 'list_sort_column', 'updated_at');
        if (!['title', 'author', 'updated_at'].includes(initialSortColumn)) {
            initialSortColumn = 'updated_at';
        }
        let initialSortOrder = queryString(routeQuery, 'list_sort_order', 'desc');
        if (!['asc', 'desc'].includes(initialSortOrder)) {
            initialSortOrder = 'desc';
        }

        return {
            loading: true,
            documentationList: [],
            filterSidebarOpen: false,
            filters: {
                search: queryString(routeQuery, 'list_search', ''),
            },
            sort: {
                column: initialSortColumn,
                order: initialSortOrder,
            },
            page: initialPage !== null && initialPage > 0 ? initialPage : 1,
            perPage: initialPerPage !== null ? initialPerPage : 10,
        };
    },
    computed: {
        filteredDocumentations() {
            const search = (this.filters.search || '').trim().toLowerCase();
            let items = (this.documentationList || []).filter((documentation) => {
                if (!search) {
                    return true;
                }

                const title = (documentation.title || '').toLowerCase();
                const author = (documentation.author || '').toLowerCase();
                return title.includes(search) || author.includes(search);
            });

            const orderFactor = this.sort.order === 'asc' ? 1 : -1;
            items = items.slice().sort((a, b) => {
                if (this.sort.column === 'title') {
                    return (a.title || '').localeCompare(b.title || '') * orderFactor;
                }
                if (this.sort.column === 'author') {
                    return (a.author || '').localeCompare(b.author || '') * orderFactor;
                }

                const timeA = a.updated_at ? new Date(a.updated_at).getTime() : 0;
                const timeB = b.updated_at ? new Date(b.updated_at).getTime() : 0;
                return (timeA - timeB) * orderFactor;
            });

            return items;
        },
        pagination() {
            const total = this.filteredDocumentations.length;
            const perPage = Number(this.perPage || 10);
            const totalPages = Math.max(1, Math.ceil(total / perPage));
            const currentPage = Math.min(this.page, totalPages);
            return {
                currentPage,
                perPage,
                total,
                totalPages,
            };
        },
        paginatedDocumentations() {
            const start = (this.pagination.currentPage - 1) * this.pagination.perPage;
            return this.filteredDocumentations.slice(start, start + this.pagination.perPage);
        },
        paginationStart() {
            if (this.pagination.total < 1) {
                return 0;
            }
            return (this.pagination.currentPage - 1) * this.pagination.perPage + 1;
        },
        paginationEnd() {
            if (this.pagination.total < 1) {
                return 0;
            }
            return Math.min(this.pagination.currentPage * this.pagination.perPage, this.pagination.total);
        },
        paginationTotal() {
            return this.pagination.total;
        },
        perPageOptions() {
            const defaults = [10, 25, 50, 100];
            const current = Number(this.perPage || 0);
            const values = defaults.includes(current) ? defaults : [...defaults, current].sort((a, b) => a - b);
            return values.map((value) => ({id: value, name: value}));
        },
        hasActiveFilters() {
            return (this.filters.search || '').trim() !== ''
                || this.sort.order !== 'desc'
                || this.sort.column !== 'updated_at'
                || Number(this.perPage || 0) !== 10;
        },
    },
    mounted() {
        this.getDocumentations();
    },
    filters: {
        momentFormatDateTimeAgo: function (value) {
            return moment(value).locale(window.app.app_date_locale).fromNow();
        },
    },
    methods: {
        toggleFilterSidebar() {
            this.filterSidebarOpen = !this.filterSidebarOpen;
        },
        documentationManagePath(documentationId) {
            return routeWithReturnTo('/documentations/' + documentationId, this.$route, '/documentations/list');
        },
        changePage(page) {
            if ((page > 0) && (page <= this.pagination.totalPages) && (page !== this.page)) {
                this.page = page;
                this.syncDocumentationListRouteState();
            }
        },
        toggleSortOrder() {
            this.sort.order = this.sort.order === 'asc' ? 'desc' : 'asc';
            this.page = 1;
            this.syncDocumentationListRouteState();
        },
        onFilterChange() {
            this.page = 1;
            this.syncDocumentationListRouteState();
        },
        onPerPageChange() {
            this.page = 1;
            this.syncDocumentationListRouteState();
        },
        resetFilters() {
            this.filters.search = '';
            this.sort.order = 'desc';
            this.sort.column = 'updated_at';
            this.perPage = 10;
            this.page = 1;
            this.syncDocumentationListRouteState();
        },
        syncDocumentationListRouteState() {
            const listRouteKeys = [
                'list_page',
                'list_per_page',
                'list_sort_column',
                'list_sort_order',
                'list_search',
            ];
            const nextQuery = queryWithoutKeys(this.$route.query || {}, listRouteKeys);

            setQueryValue(nextQuery, 'list_page', Number(this.page || 1), 1);
            setQueryValue(nextQuery, 'list_per_page', Number(this.perPage || 10), 10);
            setQueryValue(nextQuery, 'list_sort_column', this.sort.column, 'updated_at');
            setQueryValue(nextQuery, 'list_sort_order', this.sort.order, 'desc');
            setQueryValue(nextQuery, 'list_search', this.filters.search, '');

            if (sameRouteQuery(this.$route.query || {}, nextQuery)) {
                return;
            }

            const routeReplace = this.$router.replace({
                path: this.$route.path,
                query: nextQuery,
            });

            if (routeReplace && typeof routeReplace.catch === 'function') {
                routeReplace.catch(function () {
                });
            }
        },
        getDocumentations() {
            const self = this;
            self.loading = true;
            self.syncDocumentationListRouteState();
            axios.get('api/documentations').then(function (response) {
                self.documentationList = response.data;
                self.loading = false;
            }).catch(function () {
                self.documentationList = [];
                self.loading = false;
            });
        },
        getBodyPreview(value) {
            if (!value) {
                return '-';
            }

            const stripped = String(value)
                .replace(/<[^>]+>/g, ' ')
                .replace(/\s+/g, ' ')
                .trim();

            if (!stripped) {
                return '-';
            }

            const decoder = document.createElement('textarea');
            decoder.innerHTML = stripped;

            return (decoder.value || decoder.textContent || '')
                .replace(/\u00a0/g, ' ')
                .replace(/\s+/g, ' ')
                .trim() || '-';
        },
    }
}
</script>
